<script lang="ts">
  import { isSmallDot } from "./isSmallDot";

  const itemTypes = { video: "video" };
  interface Props {
    selectedDot?: number;
    amountDots?: number;
    slides?: HTMLElement[];
    isCarousel?: boolean;
    size?: "50" | "100";
    moveTo: (index: number, options: { source: "pagination-dots" }) => void;
  }

  let {
    selectedDot = 0,
    amountDots = 0,
    slides = [],
    isCarousel = false,
    size = "100",
    moveTo,
  }: Props = $props();

  let transform = $state("translateX(0)");

  const scrollWidth = $derived.by(() => (size === "50" ? 6 : 16));

  $effect(() => {
    if (selectedDot <= 2) {
      transform = `translateX(0)`;
    } else if (selectedDot >= amountDots - 2) {
      transform = `translateX(-${scrollWidth * (amountDots - 5)}px)`;
    } else {
      transform = `translateX(-${scrollWidth * (selectedDot - 2)}px)`;
    }
  });
</script>

{#if amountDots > 1}
  <div class="paginationdots" class:paginationdots--size-50={size === "50"} aria-hidden="true">
    <div class="paginationdots__wrapper" style:transform>
      <!--      eslint-disable-next-line-->
      {#if isCarousel}
        {#each slides as _, key (key)}
          <div
            role="none"
            class="paginationdots__item"
            class:paginationdots__item--size-50={size === "50"}
            class:paginationdots__item--selected={selectedDot === key}
            class:paginationdots__item--small={isSmallDot(key, selectedDot, amountDots)}
            class:paginationdots__item--video={_.dataset["ocCarouselV1.itemType"] ===
              itemTypes.video}
            class:paginationdots__item--video--selected={_.dataset["ocCarouselV1.itemType"] ===
              itemTypes.video && selectedDot === key}
            onclick={() => moveTo(key, { source: "pagination-dots" })}
          >
            {#if _.dataset["ocCarouselV1.itemType"] === itemTypes.video}
              <oc-icon-v1 type="play" size={/*                      */ "100"}></oc-icon-v1>
            {/if}
          </div>
        {/each}
      {:else}
        <!-- eslint-disable-next-line @typescript-eslint/no-unused-vars -->
        {#each Array(amountDots) as _, key (key)}
          <div
            role="none"
            class="paginationdots__item"
            class:paginationdots__item--selected={selectedDot === key}
            class:paginationdots__item--small={isSmallDot(key, selectedDot, amountDots)}
            onclick={() => moveTo(key, { source: "pagination-dots" })}
          ></div>
        {/each}
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  .paginationdots {
    box-sizing: content-box;
    max-width: calc(
      6 * tokens.$oc-component-pagination-dot-100-size-default + 6 *
        tokens.$oc-component-pagination-dot-100-gap-x
    ); /*                                                       */
    width: fit-content;
    overflow: hidden; /*                                                                */
    display: flex;
    margin: 4px auto -4px;
    padding: 4px 2px;

    &--size-50 {
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      max-width: calc(
        6 * tokens.$oc-component-pagination-dot-50-size-default + 6 *
          tokens.$oc-component-pagination-dot-50-gap-x
      );
      width: fit-content;
      background-color: tokens.$oc-component-pagination-dot-50-container-background-color;
      border-radius: tokens.$oc-component-pagination-dot-50-container-border-radius;
      padding: tokens.$oc-component-pagination-dot-50-container-spacing;
      margin: 0;
      margin-bottom: calc(tokens.$oc-component-pagination-dot-50-container-position-bottom / 2);
    }

    &__wrapper {
      display: flex;
      margin-left: auto;
      margin-right: auto;
      transition: transform 0.2s ease-in-out;
    }

    /*            */
    &__item {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: tokens.$oc-component-pagination-dot-100-size-default;
      height: tokens.$oc-component-pagination-dot-100-size-default;
      padding: 0 calc(tokens.$oc-component-pagination-dot-100-gap-x / 2);
      box-sizing: content-box;
      border: none;
      outline: none;
      background: none;
      transition: width tokens.$oc-component-pagination-dot-transition-all-duration
        tokens.$oc-component-pagination-dot-transition-all-easing;
      @include mixins.hitbox(12px);
      &:after {
        content: "";
        display: block;
        width: 100%;
        height: 100%;
        background-color: tokens.$oc-component-pagination-dot-default;
        border-radius: tokens.$oc-component-pagination-dot-100-border-radius;
        transition: all 0.2s ease-in-out;
      }

      &:not(&--selected, &--video) {
        cursor: pointer;

        @media (hover: hover) {
          &:hover:after {
            background-color: tokens.$oc-component-pagination-dot-hover;
          }
        }

        &:active:after {
          background-color: tokens.$oc-component-pagination-dot-active;
        }
      }

      &--selected {
        width: tokens.$oc-component-pagination-dot-100-width-selected;
        &:after {
          background-color: tokens.$oc-component-pagination-dot-selected;
          width: 100%;
          border-radius: tokens.$oc-component-pagination-dot-100-border-radius;
        }
      }

      &--small:after {
        width: tokens.$oc-component-pagination-dot-100-size-small;
        height: tokens.$oc-component-pagination-dot-100-size-small;
      }

      &--video {
        cursor: pointer;
        position: relative;

        oc-icon-v1 {
          color: tokens.$oc-component-pagination-dot-default;
          pointer-events: none;
          width: 12px;
          height: 12px;
          position: absolute;
        }

        @media (hover: hover) {
          &:hover oc-icon-v1 {
            color: tokens.$oc-component-pagination-dot-hover;
          }
        }

        /*                                                             */
        &:after {
          display: none;
        }

        &:active oc-icon-v1 {
          color: tokens.$oc-component-pagination-dot-active;
        }

        &--selected {
          width: tokens.$oc-component-pagination-dot-100-width-selected;

          &::after {
            display: block;
            background-color: tokens.$oc-component-pagination-dot-selected;
            width: 100%;
            height: 100%;
            border-radius: tokens.$oc-component-pagination-dot-100-border-radius;
          }

          oc-icon-v1 {
            color: tokens.$oc-component-pagination-dot-selected-play;
            position: absolute;
            width: tokens.$oc-component-pagination-dot-100-size-default;
            height: tokens.$oc-component-pagination-dot-100-size-default;
          }
        }
      }

      /*           */
      &--size-50 {
        width: tokens.$oc-component-pagination-dot-50-size-default;
        height: tokens.$oc-component-pagination-dot-50-size-default;
        padding: 0 tokens.$oc-component-pagination-dot-50-container-spacing 0 0;
        pointer-events: none;

        &:after {
          border-radius: tokens.$oc-component-pagination-dot-50-border-radius;
        }

        &.paginationdots__item--small:after {
          width: tokens.$oc-component-pagination-dot-50-size-small;
          height: tokens.$oc-component-pagination-dot-50-size-small;
        }

        &.paginationdots__item--selected {
          width: tokens.$oc-component-pagination-dot-50-width-selected;

          &:after {
            background-color: tokens.$oc-component-pagination-dot-selected;
            width: 100%;
            border-radius: tokens.$oc-component-pagination-dot-50-border-radius;
          }
        }

        &.paginationdots__item--video {
          oc-icon-v1 {
            width: 6px;
            height: 6px;
          }
        }
      }
    }
  }
</style>
